import { ConfirmDialog, Icon } from '@acs/ui';
import { useActiveBusiness } from '../../app/ActiveBusinessLayout.js';
import type { AppointmentDoc } from '../../shared/api/converters.js';
import { useI18n } from '../../i18n/index.js';
import { formatDateTime } from '../../shared/lib/format.js';
import { useCancelAppointment } from './hooks.js';

/**
 * The "are you sure?" step in front of every owner-initiated cancellation —
 * the same question whichever Cancel button was pressed (list row, calendar
 * booking dialog). ConfirmDialog portals to <body>, so it reads the same on a
 * phone as on a desktop and is never clipped by what opened it.
 *
 * `onClose` dismisses the question; `onCancelled` additionally runs once the
 * booking is actually gone, for callers with their own surface to tear down.
 */
export function CancelBookingDialog({
  businessId,
  appt,
  timezone,
  onClose,
  onCancelled,
}: {
  businessId: string;
  appt: AppointmentDoc;
  timezone: string;
  onClose(): void;
  onCancelled?(): void;
}) {
  const { t, tag } = useI18n();
  const cancel = useCancelAppointment(businessId);
  const sms = useActiveBusiness().notifications.sms;
  // Mirrors the first gates of `dispatchSms` (api/src/notifications/dispatch.ts):
  // the cancellation itself triggers the client's SMS, so promise it only when
  // that path would actually send one.
  const clientTexted = sms.enabled && sms.onCancellation && !!appt.customerPhone && !appt.test;

  return (
    <ConfirmDialog
      tone="danger"
      title={t('booking.cancelTitle')}
      description={t(clientTexted ? 'booking.cancelDetailSms' : 'booking.cancelDetail', {
        service: appt.serviceName,
        customer: appt.customerName,
        when: formatDateTime(appt.start, timezone, tag),
      })}
      icon={<Icon name="x-circle" size={24} />}
      // Never a bare "Cancel" here — both answers would read as cancelling.
      confirmLabel={t('booking.cancelConfirm')}
      cancelLabel={t('booking.cancelKeep')}
      loading={cancel.isPending}
      onCancel={onClose}
      onConfirm={() =>
        cancel.mutate(appt.id, {
          onSuccess: () => {
            onClose();
            onCancelled?.();
          },
        })
      }
    />
  );
}
